// ==========================================================================
// lib/depreciation.ts — the depreciation schedule (FF-047, 9 Oct 2026)
//   James: "we want to try build a schedule in here, as items get bought and
//   reconciled to accounts some will have a schedule to depreciate with the
//   proper rules".
//   ATO formulas (ato.gov.au, "Prime cost (straight line) and diminishing
//   value methods"):
//     diminishing value  base value × days held/365 × 200%/effective life
//                        (150% for items held before 10 May 2006)
//     prime cost         cost × days held/365 × 100%/effective life
//   Capital works: cost × days/365 × rate (2.5% for most residential building
//   work). $300 or less, mainly for rent: claimed in full the first year.
//   Low-value pool (under $1,000): 18.75% in the year added, 37.5% after.
//   Second-hand items in a residential rental after 9 May 2017: not claimable.
//   This is a starting point for the accountant, not tax advice.
// ==========================================================================
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "./supabase";
import { useHouseholdId } from "./household";
import { fyOf, fyRange } from "./vehicles";

export type DepItem = {
  id: string; asset_id: string | null; name: string; kind: "plant" | "capital_works"; treatment: "depreciate" | "immediate" | "pool";
  method: "dv" | "pc"; ready_on: string; cost_cents: number; effective_life_years: number | null; rate_pct: number | null; use_pct: number;
  second_hand: boolean; ledger_id: string | null; transaction_id: string | null; life_source: string | null;
  disposed_on: string | null; disposal_cents: number | null; notes: string | null; archived_at: string | null;
};

/** Effective lives from the ATO's "Residential rental property items" tables
 *  (assets acquired from 1 July 2019 unless noted). Check the ATO page for anything not here. */
export const LIFE_PRESETS: { name: string; years: number; note?: string }[] = [
  { name: "Air conditioner, split system (up to 20kW)", years: 10 },
  { name: "Air conditioner, room unit", years: 10 },
  { name: "Ceiling fan", years: 5 },
  { name: "Carpet", years: 8 },
  { name: "Cooktop", years: 12 },
  { name: "Curtains", years: 6 },
  { name: "Dishwasher", years: 8 },
  { name: "Garbage disposal unit", years: 10 },
  { name: "Hot water system, electric or gas", years: 12 },
  { name: "Hot water system, solar", years: 15 },
  { name: "Oven", years: 12 },
  { name: "Smoke alarm", years: 6, note: "ATO table lists 6 years for 2004–2019; check for later items" },
  { name: "Washing machine", years: 8 },
  { name: "Window blinds, internal", years: 10 },
];

const DAY = 86400000;
const d = (s: string) => new Date(`${s}T00:00:00`);
const daysBetween = (a: Date, b: Date) => Math.round((b.getTime() - a.getTime()) / DAY) + 1; // inclusive

/** Days the item was held (ready for use) in a financial year. */
function daysIn(item: DepItem, fy: string) {
  const r = fyRange(fy);
  const from = d(item.ready_on) > d(r.from) ? d(item.ready_on) : d(r.from);
  const end = item.disposed_on && d(item.disposed_on) < d(r.to) ? d(item.disposed_on) : d(r.to);
  return end < from ? 0 : daysBetween(from, end);
}

export type DepYear = { fy: string; opening: number; decline: number; claim: number; closing: number; days: number };

/** The item's schedule from the year it was ready for use to `untilFy` (in cents; claim = decline × use %). */
export function scheduleFor(item: DepItem, untilFy: string): DepYear[] {
  const out: DepYear[] = [];
  const first = fyOf(d(item.ready_on));
  let fy = first; let value = item.cost_cents; let guard = 0;
  const next = (f: string) => { const y = Number(f.slice(0, 4)) + 1; return `${y}-${String((y + 1) % 100).padStart(2, "0")}`; };
  while (fy <= untilFy && guard++ < 80) {
    const days = daysIn(item, fy); const opening = value;
    let decline = 0;
    if (item.second_hand || days === 0 || opening <= 0) decline = 0;
    else if (item.treatment === "immediate") decline = fy === first ? opening : 0;
    else if (item.treatment === "pool") decline = Math.round(opening * (fy === first ? 0.1875 : 0.375));
    else if (item.kind === "capital_works") decline = Math.round(item.cost_cents * (days / 365) * ((item.rate_pct ?? 2.5) / 100));
    else if (item.effective_life_years) {
      const life = item.effective_life_years;
      if (item.method === "pc") decline = Math.round(item.cost_cents * (days / 365) * (1 / life));
      else decline = Math.round(opening * (days / 365) * ((item.ready_on < "2006-05-10" ? 1.5 : 2) / life));
    }
    decline = Math.min(decline, opening);
    value = opening - decline;
    out.push({ fy, opening, decline, claim: Math.round(decline * (item.use_pct / 100)), closing: value, days });
    if (item.disposed_on && fyOf(d(item.disposed_on)) === fy) break;
    fy = next(fy);
  }
  return out;
}

/** This FY's line for an item (or null if it isn't held that year). */
export const yearOf = (item: DepItem, fy: string) => scheduleFor(item, fy).find((y) => y.fy === fy) ?? null;

/** Suggested treatment for a cost: $300 or less → immediate; otherwise depreciate (pool is a choice). */
export const suggestTreatment = (cents: number): DepItem["treatment"] => (cents <= 30000 ? "immediate" : "depreciate");

export function useDepItems(assetId: string | undefined) {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["depreciation", hid, assetId], enabled: !!hid && !!assetId,
    queryFn: async () => {
      const { data, error } = await supabase.from("depreciating_items")
        .select("id,asset_id,name,kind,treatment,method,ready_on,cost_cents,effective_life_years,rate_pct,use_pct,second_hand,ledger_id,transaction_id,life_source,disposed_on,disposal_cents,notes,archived_at")
        .eq("asset_id", assetId!).is("archived_at", null).order("ready_on");
      if (error) throw error;
      return (data as DepItem[]).map((x) => ({ ...x, cost_cents: Number(x.cost_cents), effective_life_years: x.effective_life_years === null ? null : Number(x.effective_life_years),
        rate_pct: x.rate_pct === null ? null : Number(x.rate_pct), use_pct: Number(x.use_pct), disposal_cents: x.disposal_cents === null ? null : Number(x.disposal_cents) }));
    },
  });
}

export type DepDraft = Omit<DepItem, "id" | "archived_at"> & { id?: string };

export function useSaveDepItem() {
  const hid = useHouseholdId(); const qc = useQueryClient();
  return useMutation({
    mutationFn: async (x: DepDraft) => {
      const { id, ...row } = x;
      const { error } = id ? await supabase.from("depreciating_items").update(row).eq("id", id) : await supabase.from("depreciating_items").insert({ ...row, household_id: hid });
      if (error) throw error.code === "23505" ? new Error("That line is already in the schedule.") : error;
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["depreciation", hid] }); void qc.invalidateQueries({ queryKey: ["audit", hid] }); },
  });
}

export function useArchiveDepItem() {
  const hid = useHouseholdId(); const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("depreciating_items").update({ archived_at: new Date().toISOString() }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["depreciation", hid] }); },
  });
}

/** Best ATO preset for a description ("New air conditioner" → split system). */
export function presetFor(text: string) {
  const t = text.toLowerCase();
  const keys: [RegExp, string][] = [
    [/air.?con|split system|a\/c\b/, "Air conditioner, split system (up to 20kW)"], [/ceiling fan/, "Ceiling fan"], [/carpet/, "Carpet"],
    [/cook.?top/, "Cooktop"], [/curtain/, "Curtains"], [/dish.?washer/, "Dishwasher"], [/garbage disposal/, "Garbage disposal unit"],
    [/solar hot water/, "Hot water system, solar"], [/hot water/, "Hot water system, electric or gas"], [/oven/, "Oven"],
    [/smoke alarm/, "Smoke alarm"], [/washing machine/, "Washing machine"], [/blind/, "Window blinds, internal"],
  ];
  const hit = keys.find(([re]) => re.test(t));
  return hit ? LIFE_PRESETS.find((p) => p.name === hit[1]) : undefined;
}
